import { useState } from 'react'
import { Alert, Button, Col, Row, Space, Tag, Tooltip, Typography } from 'antd'
import { ArrowLeftOutlined, SendOutlined, BellOutlined } from '@ant-design/icons'
import { Show } from '@refinedev/antd'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { DetailNotFound } from '../../components/DetailNotFound'
import { PageError } from '../../components/PageError'
import { StatusBadge } from '../../components/StatusBadge'
import { useBackToList } from '../../shared/hooks/useBackToList'
import { useFoDisplayStatusConfig } from './foDisplayStatusConfig'
import { FoAnnotationBadge } from './FoAnnotationBadge'
import { OrderInfoCard } from './OrderInfoCard'
import { OrderItemsCard } from './OrderItemsCard'
import { BuyerInfoCard } from './BuyerInfoCard'
import { BuyerLink } from '../../components/BuyerLink'
import { NicknameText } from '../../components/NicknameText'
import { BuyerGroupFoCard } from './BuyerGroupFoCard'
import { BuyerGroupSummaryCard } from './BuyerGroupSummaryCard'
import { ShippingAddressCard } from './ShippingAddressCard'
import { ShippingTrackingCard } from './ShippingTrackingCard'
import { PaymentInfoCard } from './PaymentInfoCard'
import { ShipOrderModal } from './ShipOrderModal'
import { ChangeTrackingModal } from './ChangeTrackingModal'
import { ChangeShippingAddressModal } from './ChangeShippingAddressModal'
import { useRequestConfirmation } from './useRequestConfirmation'
import { formatKstDateTime } from '../../utils/formatKstDateTime'
import { useOrderDetailData } from './useOrderDetailData'
import { formatFoContext, formatFoTitle } from './orderDisplay'
import { findUnendedBroadcastLabels, summarizeBuyerGroup } from './buyerGroupSummary'
import { confirmPopup } from '@/components/popup'

export function OrderDetail() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const buyerId = searchParams.get('buyerId')
  const navigate = useNavigate()
  const backToList = useBackToList('/orders')
  const { t } = useTranslation()
  const statusConfig = useFoDisplayStatusConfig()

  const [shipModalOpen, setShipModalOpen] = useState(false)
  const [changeTrackingOpen, setChangeTrackingOpen] = useState(false)
  const [changeAddressOpen, setChangeAddressOpen] = useState(false)

  const { requestConfirmation, isRequesting } = useRequestConfirmation()
  const { order, groupOrders, isGroupMode, isLoading, isError, refetch } = useOrderDetailData(
    id,
    buyerId,
  )

  if (!isLoading && isError) return <PageError onRetry={refetch} />
  if (!isLoading && !isError && !order) return <DetailNotFound backTo="/orders" />

  // Shipping is a buyer-wide action: the server ships every unshipped PAID FO of this buyer.
  const shipTargets = isGroupMode ? groupOrders : order ? [order] : []
  const groupSummary = isGroupMode ? summarizeBuyerGroup(groupOrders) : null
  const blockingBroadcasts = findUnendedBroadcastLabels(shipTargets, t)

  const canShip = order?.displayStatus === 'PAID' && blockingBroadcasts.length === 0
  // Delivery completion is the 06:00 batch's job now (#46 U4·U5); the tracking number can still be
  // corrected while the parcel is out.
  const canChangeTracking = order?.displayStatus === 'SHIPPED'
  // #87: the parcel's address moves only before it ships (server enforces the same, 409 otherwise).
  const canChangeAddress = order?.displayStatus === 'PAID'
  // Server decision (#46 D10·D12). Undefined on an older server → the button is not offered.
  const isConfirmable = order?.displayStatus === 'SHIPPED' || order?.displayStatus === 'DELIVERED'
  const showRequestConfirmation = isConfirmable && order?.canRequestConfirmation !== undefined
  const requestConfirmationDisabledReason = !order?.canRequestConfirmation
    ? order?.nextConfirmationRequestAvailableAt
      ? t('common.modals.requestConfirmationNextAt', {
          at: formatKstDateTime(order.nextConfirmationRequestAvailableAt),
        })
      : t('common.modals.requestConfirmationUnavailable')
    : null

  const handleRequestConfirmation = () => {
    if (!id) return
    confirmPopup({
      title: t('common.modals.requestConfirmationTitle'),
      content: t('common.modals.requestConfirmationMessage'),
      okText: t('common.buttons.confirm'),
      cancelText: t('common.buttons.cancel'),
      okButtonProps: { 'data-testid': 'order-detail-request-confirmation-confirm-btn' },
      cancelButtonProps: { 'data-testid': 'order-detail-request-confirmation-cancel-btn' },
      onOk: () => requestConfirmation(id),
    })
  }

  return (
    <Show
      isLoading={isLoading}
      title={t('pages.orders.detailTitle')}
      headerButtons={
        <Space>
          {order?.displayStatus === 'PAID' && (
            <Button
              data-testid="order-detail-ship-btn"
              type="primary"
              icon={<SendOutlined />}
              disabled={!canShip}
              onClick={() => setShipModalOpen(true)}
            >
              {shipTargets.length > 1
                ? t('pages.orders.groupShipButton', { count: shipTargets.length })
                : t('common.buttons.ship')}
            </Button>
          )}
          {isConfirmable && order?.autoConfirmScheduledAt && (
            <Typography.Text type="secondary" data-testid="order-detail-auto-confirm-at">
              {t('common.modals.autoConfirmScheduledAt', {
                at: formatKstDateTime(order.autoConfirmScheduledAt),
              })}
            </Typography.Text>
          )}
          {showRequestConfirmation && (
            <Tooltip title={requestConfirmationDisabledReason}>
              <Button
                data-testid="order-detail-request-confirmation-btn"
                type="primary"
                icon={<BellOutlined />}
                loading={isRequesting}
                disabled={!order?.canRequestConfirmation}
                onClick={handleRequestConfirmation}
              >
                {t('common.buttons.requestConfirmation')}
              </Button>
            </Tooltip>
          )}
        </Space>
      }
    >
      <Button
        data-testid="order-detail-back-btn"
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={backToList}
        style={{ padding: 0, marginBottom: 16 }}
      >
        {t('common.buttons.backToList')}
      </Button>

      {order && (
        <>
          <Space style={{ marginBottom: 16 }} wrap>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {isGroupMode ? (
                <BuyerLink buyer={order.buyer} strong />
              ) : (
                <NicknameText nickname={order.buyer.nickname}>{formatFoTitle(order)}</NicknameText>
              )}
            </Typography.Title>
            {isGroupMode ? (
              <Tag color="blue">{t('pages.orders.foCount', { count: groupOrders.length })}</Tag>
            ) : (
              <>
                <StatusBadge status={order.displayStatus} configMap={statusConfig} />
                <FoAnnotationBadge annotation={order.annotation} />
              </>
            )}
          </Space>

          {blockingBroadcasts.length > 0 && (
            <Alert
              data-testid="order-detail-broadcast-warning"
              type="warning"
              message={t('common.modals.broadcastNotEndedDetail', {
                names: blockingBroadcasts.join(', '),
              })}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {groupSummary?.hasAddressMismatch && (
            <Alert
              data-testid="order-detail-address-mismatch-warning"
              type="warning"
              message={t('pages.orders.groupSummary.addressMismatch')}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          {isGroupMode && groupSummary ? (
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <BuyerGroupSummaryCard summary={groupSummary} />
              </Col>
              <Col xs={24} lg={12}>
                <BuyerInfoCard order={order} />
              </Col>
              {(groupSummary.commonAddress || canChangeAddress) && (
                <Col xs={24} lg={12}>
                  <ShippingAddressCard
                    address={groupSummary.commonAddress}
                    onChange={canChangeAddress ? () => setChangeAddressOpen(true) : undefined}
                  />
                </Col>
              )}
              {groupOrders.map((groupOrder) => (
                <Col xs={24} key={groupOrder.fulfillmentOrderId}>
                  <BuyerGroupFoCard order={groupOrder} />
                </Col>
              ))}
            </Row>
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <OrderInfoCard order={order} />
              </Col>
              {(order.parcelFulfillmentOrderIds?.length ?? 1) > 1 && order.parcelSummary && (
                // #78: one parcel carries several fulfillment orders — show the parcel as the server
                // sums it and link the other members, so this screen matches the app's parcel view.
                <Col xs={24} lg={12}>
                  <BuyerGroupSummaryCard
                    summary={{
                      fulfillmentOrderCount: order.parcelSummary.fulfillmentOrderIds.length,
                      orderCount: order.parcelSummary.orderCount,
                      totalAmount: order.parcelSummary.totalAmount.amount,
                      commonAddress: order.shippingAddress ?? null,
                      hasAddressMismatch: false,
                    }}
                  />
                  <Space wrap style={{ marginTop: 8 }} data-testid="order-detail-parcel-members">
                    <Typography.Text type="secondary">
                      {t('pages.orders.parcelMembers')}
                    </Typography.Text>
                    {order.parcelSummary.fulfillmentOrderIds
                      .filter((memberId) => memberId !== order.fulfillmentOrderId)
                      .map((memberId) => (
                        <Link key={memberId} to={`/orders/${memberId}`}>
                          {memberId.slice(0, 8)}
                        </Link>
                      ))}
                  </Space>
                </Col>
              )}
              <Col xs={24}>
                <Typography.Text type="secondary">{formatFoContext(order, t)}</Typography.Text>
                <OrderItemsCard order={order} />
              </Col>
              <Col xs={24} lg={12}>
                <BuyerInfoCard order={order} />
              </Col>
              {(order.shippingAddress || canChangeAddress) && (
                <Col xs={24} lg={12}>
                  <ShippingAddressCard
                    address={order.shippingAddress ?? null}
                    onChange={canChangeAddress ? () => setChangeAddressOpen(true) : undefined}
                  />
                </Col>
              )}
              {order.shippingOption && (
                <Col xs={24} lg={12}>
                  <ShippingTrackingCard
                    order={order}
                    onChangeTracking={
                      canChangeTracking ? () => setChangeTrackingOpen(true) : undefined
                    }
                  />
                </Col>
              )}
              <Col xs={24} lg={12}>
                <PaymentInfoCard order={order} />
              </Col>
            </Row>
          )}

          <ShipOrderModal
            fulfillmentOrderId={order.fulfillmentOrderId}
            targetCount={shipTargets.length}
            open={shipModalOpen}
            onClose={() => setShipModalOpen(false)}
            onShipped={(result) => {
              // The buyer new-order group no longer exists after shipping; land on the
              // representative FO's detail (now SHIPPED, with the tracking card). `replace`
              // keeps the Back button on the list instead of the now-empty ?buyerId URL.
              void navigate(`/orders/${result.fulfillmentOrderId}`, { replace: true })
            }}
          />
          {changeAddressOpen && (
            <ChangeShippingAddressModal
              fulfillmentOrderId={order.fulfillmentOrderId}
              currentAddressId={order.shippingAddress?.addressId ?? null}
              open={changeAddressOpen}
              onClose={() => setChangeAddressOpen(false)}
            />
          )}
          {changeTrackingOpen && (
            <ChangeTrackingModal
              fulfillmentOrderId={order.fulfillmentOrderId}
              open={changeTrackingOpen}
              onClose={() => setChangeTrackingOpen(false)}
              defaultCarrier={order.carrier ?? null}
              defaultTrackingNumber={order.trackingNumber ?? null}
            />
          )}
        </>
      )}
    </Show>
  )
}
